import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  Box,
  Button,
  Card,
  Chip,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { StyledInputLabel } from '../../../components/inputs/InputLabel';
import { AuthApiError } from '../../../contexts/auth/authApi';
import { useAuth } from '../../../contexts/auth/useAuth';
import { CodeInput, isCompleteCode } from './CodeInput';
import { authErrorKey } from './authErrorMessage';

const isStaging = import.meta.env.REACT_APP_BUILD === 'staging';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_S = 30;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

const EmailLabel = styled(StyledInputLabel)`
  font-weight: 600;
`;

export function SignInCard() {
  const { t } = useTranslation();
  const { startPasswordless, verifyOtp, sessionExpired } = useAuth();
  const [step, setStep] = useState<'email' | 'code' | 'noAccess'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);
  const [refocusCode, setRefocusCode] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const autoSubmitted = useRef<string | null>(null);
  const isFirstRender = useRef(true);

  // Move focus to the new heading when the step changes (not on first load).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  // The boxes are disabled while a code is checked, so focus can only move
  // back to the first one once they're enabled again.
  useEffect(() => {
    if (!refocusCode || pending) return;
    setRefocusCode(false);
    document.getElementById('welcome-code-0')?.focus();
  }, [refocusCode, pending]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const errorText = (err: unknown) =>
    t(`auth.welcome.errors.${authErrorKey(err)}`);

  const sendCode = async () => {
    await startPasswordless(email.trim().toLowerCase());
    setCooldown(RESEND_COOLDOWN_S);
  };

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (pending) return;
    if (!EMAIL_PATTERN.test(email.trim())) {
      setError(t('auth.welcome.errors.invalid_email'));
      return;
    }
    setError(null);
    setPending(true);
    try {
      await sendCode();
      setCode('');
      setResent(false);
      autoSubmitted.current = null;
      setStep('code');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setPending(false);
    }
  };

  const submitCode = async (value: string) => {
    if (pending) return;
    if (!isCompleteCode(value, CODE_LENGTH)) {
      setError(t('auth.welcome.errors.incomplete_code'));
      return;
    }
    setError(null);
    setPending(true);
    try {
      // Signed in: Welcome sees isAuthenticated and goes to returnTo.
      await verifyOtp(email.trim().toLowerCase(), value);
    } catch (err) {
      setPending(false);
      // The require_account Action denies users with no JST account.
      if (err instanceof AuthApiError && err.code === 'access_denied') {
        setStep('noAccess');
        return;
      }
      // Left in the boxes, a wrong code is completed again by the first digit
      // retyped and auto-submitted, so start over from an empty first box.
      if (authErrorKey(err) === 'invalid_code') {
        setCode('');
        autoSubmitted.current = null;
        setRefocusCode(true);
      }
      setError(errorText(err));
    }
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    if (error) setError(null);
    // Submit once when the code is complete, but not again for the same code.
    if (isCompleteCode(value, CODE_LENGTH) && autoSubmitted.current !== value) {
      autoSubmitted.current = value;
      void submitCode(value);
    }
  };

  const handleResend = async () => {
    if (pending || cooldown > 0) return;
    setError(null);
    setResent(false);
    setPending(true);
    try {
      await sendCode();
      setCode('');
      autoSubmitted.current = null;
      setResent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setPending(false);
    }
  };

  const handleDifferentEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
    setResent(false);
  };

  // The code is exchanged through our API, so there's no Auth0 browser
  // session to log out of; starting again with a new email is enough.
  if (step === 'noAccess') {
    return (
      <Card
        variant="default"
        component="section"
        sx={{
          width: '100%',
          maxWidth: { md: 400 },
          p: 32,
          border: 'none',
          borderRadius: '6px',
          boxShadow:
            '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h3"
          component="h2"
          ref={headingRef}
          tabIndex={-1}
          sx={{ mb: 8, outline: 'none' }}
        >
          {t('auth.welcome.noAccess.heading')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 24 }}>
          {t('auth.welcome.noAccess.message')}
        </Typography>
        <Button
          variant="primary"
          size="large"
          fullWidth
          onClick={() => {
            setEmail('');
            handleDifferentEmail();
          }}
        >
          {t('auth.welcome.noAccess.differentEmail')}
        </Button>
      </Card>
    );
  }

  return (
    <Card
      variant="default"
      component="section"
      aria-busy={pending}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: { md: 400 },
        p: 32,
        border: 'none',
        borderRadius: '6px',
        boxShadow:
          '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      }}
    >
      {step === 'email' ? (
        <Box component="form" noValidate onSubmit={handleEmailSubmit}>
          <Typography
            variant="h4"
            component="h2"
            ref={headingRef}
            tabIndex={-1}
            sx={{ mb: 8, outline: 'none', textAlign: 'center' }}
          >
            {t('auth.welcome.email.heading')}
          </Typography>
          {isStaging && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Chip
                label={t('auth.welcome.email.stagingBadge')}
                size="small"
                variant="secondary"
              />
            </Box>
          )}
          {sessionExpired && (
            <Typography
              role="status"
              variant="body2"
              sx={{
                mt: 16,
                p: 12,
                borderRadius: '6px',
                backgroundColor: 'var(--color-very-pale-orange)',
              }}
            >
              {t('auth.welcome.errors.session_expired')}
            </Typography>
          )}
          <EmailLabel htmlFor="welcome-email" sx={{ mt: 24 }}>
            {t('auth.welcome.email.label')} <span aria-hidden="true">*</span>
          </EmailLabel>
          <TextField
            id="welcome-email"
            name="email"
            required
            placeholder={t('auth.welcome.email.placeholder') as string}
            type="email"
            autoComplete="email"
            autoFocus
            fullWidth
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
            error={!!error}
            helperText={error ?? ' '}
            FormHelperTextProps={{ role: error ? 'alert' : undefined }}
          />
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={pending}
            sx={{ mt: 8 }}
          >
            {pending
              ? t('auth.welcome.email.pending')
              : t('auth.welcome.email.submit')}
          </Button>
        </Box>
      ) : (
        <Box
          component="form"
          noValidate
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            void submitCode(code);
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            ref={headingRef}
            tabIndex={-1}
            sx={{ mb: 8, outline: 'none', textAlign: 'center' }}
          >
            {t('auth.welcome.code.heading')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 24, wordBreak: 'break-word' }}>
            <Trans
              i18nKey="auth.welcome.code.sent"
              values={{ email: email.trim().toLowerCase() }}
              components={{ strong: <strong /> }}
            />
          </Typography>
          <CodeInput
            id="welcome-code"
            length={CODE_LENGTH}
            value={code}
            onChange={handleCodeChange}
            error={error}
            disabled={pending}
          />
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={pending}
          >
            {pending
              ? t('auth.welcome.code.pending')
              : t('auth.welcome.code.submit')}
          </Button>
          <Box
            sx={{
              mt: 16,
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={handleResend}
              disabled={pending || cooldown > 0}
              sx={{
                mb: 8,
                '&:disabled': {
                  color: 'var(--color-dark-greyish-blue)',
                  textDecoration: 'none',
                  cursor: 'default',
                },
              }}
            >
              {t('auth.welcome.code.resend')}
              {cooldown > 0 && <span aria-hidden="true"> ({cooldown} s)</span>}
            </Link>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={handleDifferentEmail}
              // A code being checked can't be cancelled, and would still sign
              // in as the first email.
              disabled={pending}
              sx={{
                mb: 8,
                '&:disabled': {
                  color: 'var(--color-dark-greyish-blue)',
                  textDecoration: 'none',
                  cursor: 'default',
                },
              }}
            >
              {t('auth.welcome.code.differentEmail')}
            </Link>
          </Box>
          {/* Announced once per send, not on every tick of the countdown. */}
          <Box role="status" aria-live="polite" sx={visuallyHidden}>
            {resent ? t('auth.welcome.code.resent') : ''}{' '}
            {cooldown > 0
              ? t('auth.welcome.code.resendIn', { seconds: RESEND_COOLDOWN_S })
              : ''}
          </Box>
        </Box>
      )}
    </Card>
  );
}
